/**
 * InPlaceAnimationOverlay - Renders animation directly on the editor canvas.
 * Manages viewport save/restore and owns the animation lifecycle.
 */

import { useEffect, useMemo, useRef } from 'react'
import { ExternalAnimationCanvas } from './ExternalAnimationCanvas'
import { AnimationControlBar } from './AnimationControlBar'
import { useAnimationInstance } from '@/hooks/UseAnimationInstance'
import { useCanvasViewport } from '@/contexts/CanvasViewportContext'
import type { Drawing, Player } from '@/types'
import './in-place-animation-overlay.css'

type InPlaceAnimationOverlayProps = {
	playId: string
	players: Player[]
	drawings: Drawing[]
	onStop: () => void
}

export function InPlaceAnimationOverlay({
	playId,
	players,
	drawings,
	onStop,
}: InPlaceAnimationOverlayProps) {
	const { zoom, panX, panY, resetViewport, setZoom, setPan } = useCanvasViewport()

	// Capture viewport on mount, restore on unmount
	const savedViewportRef = useRef({ zoom, panX, panY })

	useEffect(() => {
		// Save current viewport and reset to 1:1
		savedViewportRef.current = { zoom, panX, panY }
		resetViewport()

		return () => {
			// Restore previous viewport
			const { zoom: savedZoom, panX: savedPanX, panY: savedPanY } = savedViewportRef.current
			setZoom(savedZoom)
			setPan(savedPanX, savedPanY)
		}
		// Only run on mount/unmount — intentionally exclude reactive deps
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Memoize playData to prevent useAnimationTiming from recalculating every render
	// (its useMemo depends on reference equality of playData)
	const playData = useMemo(
		() => ({ id: playId, players, drawings }),
		[playId, players, drawings],
	)

	// Create standalone animation instance
	const instance = useAnimationInstance({
		playData,
		autoPlay: true,
	})

	return (
		<div className="in-place-animation-overlay">
			<ExternalAnimationCanvas
				drawings={drawings}
				players={players}
				animationState={instance.state}
			/>
			<AnimationControlBar
				instance={instance}
				onStop={onStop}
			/>
		</div>
	)
}
